import { TFunction } from 'i18next';
import _ from 'lodash';
import moment from 'moment';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from '@treams/treams-network';
import { EvaluationSubmissionsFutureForecastQuery } from '@treams/treams-network';
import {
	Callout,
	Container,
	Count,
	FlexGap,
	GraphQLErrorMessage,
	PAGE_CONTENT_WIDTH,
	Spinner,
	Subheading,
	Switch,
	Table,
	tokens,
} from '@treams/treams-ui';

import {
	ConversationsTableEmptyRow,
	ConversationsTableHeader,
	ConversationsTableMomentRow,
	ConversationsTableRow,
} from '../conversations/ConversationsTable';

const CALLOUT_STORAGE_KEY = 'manager.conversations.forecast.callout';

const Section = styled(Box)`
	display: flex;
	flex-direction: column;
	gap: ${tokens.gap.group}px;
`;

const SectionHeader = styled(FlexGap)`
	align-items: center;
`;

type DateSection = {
	before?: moment.Moment;
	after?: moment.Moment;
	label: string;
	isDue?: boolean;
	isCompleted?: boolean;
	isToday?: boolean;
};

function findNearestSection(
	evaluationSubmission: graphql.EvaluationSubmission | graphql.MomentInstance,
	sections: DateSection[],
): DateSection {
	if (
		evaluationSubmission.__typename === 'EvaluationSubmission' &&
		evaluationSubmission.manager.isCompleted
	) {
		return sections[0];
	}

	const deadline =
		evaluationSubmission.__typename === 'EvaluationSubmission'
			? evaluationSubmission.deadline
			: evaluationSubmission.__typename === 'MomentInstance'
				? evaluationSubmission.endDate
				: undefined;

	return sections.slice(1, sections.length - 1).reduce(
		(result: DateSection, section: DateSection) => {
			if (moment(deadline).isBetween(section.after, section.before, undefined, '[]')) {
				return section;
			}
			return result;
		},
		sections[sections.length - 1],
	);
}

function dateSections(t: TFunction) {
	const now = moment();
	const thisWeek = moment(now).endOf('day');
	const nextWeek = moment(thisWeek).add(1, 'week');
	const later = moment(nextWeek).add(1, 'week');

	return [
		{
			isCompleted: true,
			label: t('manager.conversations.forecast.section.completed'),
		},
		{
			after: moment('20000101', 'YYYYMMDD'),
			before: moment(now).subtract(1, 'days').endOf('day'),
			isDue: true,
			label: t('manager.conversations.forecast.section.due'),
		},
		{
			after: moment(now).startOf('day'),
			before: moment(now).endOf('day'),
			isToday: true,
			label: t('manager.conversations.forecast.section.today'),
		},
		{
			// Explicitly not startOf('week') as we only want to return dates after today
			after: moment(thisWeek),
			before: moment(thisWeek).endOf('week'),
			label: t('manager.conversations.forecast.section.thisWeek', {
				startDate: moment(thisWeek).startOf('week').format('LL'),
				endDate: moment(thisWeek).endOf('week').format('LL'),
			}),
		},
		{
			after: moment(nextWeek).startOf('week'),
			before: moment(nextWeek).endOf('week'),
			label: t('manager.conversations.forecast.section.nextWeek', {
				startDate: moment(nextWeek).startOf('week').format('LL'),
				endDate: moment(nextWeek).endOf('week').format('LL'),
			}),
		},
		{
			after: moment(later).startOf('week'),
			before: moment(later).add(3, 'years'),
			label: t('manager.conversations.forecast.section.later'),
		},
	];
}

function groupedByDate(
	items: (graphql.EvaluationSubmission | graphql.MomentInstance)[],
	sections: DateSection[],
): Record<string, (graphql.EvaluationSubmission | graphql.MomentInstance)[]> {
	return _.groupBy(
		items,
		(evaluationSubmission) => findNearestSection(evaluationSubmission, sections).label,
	);
}

type ManagerCalendarItemsProps = {
	forecastData: EvaluationSubmissionsFutureForecastQuery | undefined;
};

function ManagerCalendarItems({ forecastData }: ManagerCalendarItemsProps) {
	const { t } = useTranslation();

	const [archive] = graphql.useArchiveEvaluationSubmissionMutation();
	const [skip] = graphql.useSkipMomentInstanceMutation();
	const [complete] = graphql.useCompleteConversationMutation();
	const [uncomplete] = graphql.useUncompleteConversationMutation();

	const [showCompleted, setShowCompleted] = useState(false);
	const [calloutVisible, setCalloutVisible] = useState(
		() => localStorage.getItem(CALLOUT_STORAGE_KEY) !== 'hidden',
	);

	const sections = dateSections(t);

	let submissions = forecastData
		? _.concat(
				forecastData.viewer.outgoingEvaluationSubmissions.edges
					.map(({ node }) => node as graphql.EvaluationSubmission)
					.filter((submission) => !submission.isArchived) as (
					| graphql.EvaluationSubmission
					| graphql.MomentInstance
				)[],
				forecastData.viewer.managees.flatMap((managee) =>
					managee.moments.map((moment) => ({ ...moment, user: managee }) as graphql.MomentInstance),
				),
			)
		: [];

	submissions = _.uniqBy(submissions, (submission) => submission.id);

	submissions = _.sortBy(submissions, (submission) =>
		'deadline' in submission ? submission.deadline : submission.endDate,
	);

	const groupedSubmissions = forecastData ? groupedByDate(submissions, sections) : {};
	const hasCompleted = Boolean(
		sections.find((section) => section.isCompleted && groupedSubmissions[section.label]),
	);

	return (
		<FlexGap flexDirection="column" gap={tokens.gap.section}>
			{calloutVisible && (
				<Callout
					color="blue"
					onDismiss={() => {
						localStorage.setItem(CALLOUT_STORAGE_KEY, 'hidden');
						setCalloutVisible(false);
					}}
				>
					{t('manager.conversations.forecast.markAsCompleted.explanation')}
				</Callout>
			)}

			{hasCompleted && (
				<Switch
					on={showCompleted}
					onChange={setShowCompleted}
					label={t('manager.conversations.forecast.showCompleted')}
				/>
			)}

			{sections
				.filter((section) => groupedSubmissions[section.label] !== undefined || section.isToday)
				.filter((section) => !section.isCompleted || showCompleted)
				.map((section) => {
					const items = groupedSubmissions[section.label] ?? [];
					const count = items.length;

					return (
						<Section key={section.label}>
							<SectionHeader gap={tokens.gap.element}>
								<Subheading>{section.label}</Subheading>
								{section.isDue && count > 0 && <Count count={count} />}
							</SectionHeader>
							<Table>
								<ConversationsTableHeader />
								{section.isToday && items.length === 0 && (
									<ConversationsTableEmptyRow
										message={t('manager.conversations.forecast.empty.today')}
									/>
								)}
								{items.map((evaluationSubmission) =>
									evaluationSubmission.__typename === 'EvaluationSubmission' ? (
										<ConversationsTableRow
											key={evaluationSubmission.id}
											evaluationSubmission={evaluationSubmission}
											onArchive={async ({ id }) => {
												await archive({ variables: { input: { id } } });
											}}
											onComplete={async ({ id }) => {
												await complete({ variables: { input: { id } } });
											}}
											onUncomplete={async ({ id }) => {
												await uncomplete({ variables: { input: { id } } });
											}}
										/>
									) : (
										<ConversationsTableMomentRow
											key={evaluationSubmission.id}
											momentInstance={evaluationSubmission as graphql.MomentInstance}
											onSkip={async ({ id }) => {
												await skip({
													variables: {
														input: {
															id,
															skipReason: graphql.MomentSkipReason.Other,
															skipReasonOther: '-',
														},
													},
												});
											}}
										/>
									),
								)}
							</Table>
						</Section>
					);
				})}
		</FlexGap>
	);
}

export default function ManagerCalendar() {
	const after = useRef(moment().subtract(3, 'months')).current;

	const forecast = graphql.useEvaluationSubmissionsFutureForecastQuery({
		variables: {
			isArchived: false,
			after,
		},
	});

	return (
		<Container wide={PAGE_CONTENT_WIDTH} mt={`${tokens.gap.page}px`} mb={`${tokens.gap.page}px`}>
			{forecast.loading && <Spinner centered={true} />}
			{forecast.error && <GraphQLErrorMessage error={forecast.error.graphQLErrors[0]} />}
			{!forecast.loading && !forecast.error && (
				<ManagerCalendarItems forecastData={forecast.data} />
			)}
		</Container>
	);
}
